import {
  Component,
  Input,
  ViewChild,
  ElementRef,
  Inject,
  PLATFORM_ID,
  ChangeDetectorRef,
  NgZone,
  OnDestroy
} from '@angular/core';
import { FormGroup } from '@angular/forms';
import { base64ToFile } from 'ngx-image-cropper';
import {
  FEED_PREVIEW_IMAGE_ORIENTATION,
  FEED_PREVIEW_IMAGE_TYPE,
  Fields,
  validationMessage
} from '../../../../constants/constant';
import { CommonService } from '../../../shared/services/common.service';
import { isPlatformBrowser } from '@angular/common';
import { Messages } from '../../../../enums/messages.enums';
import { Application, BucketFolders } from 'src/app/enums/application.enums';
import Compressor from 'compressorjs';
import { base64ToImage } from '@utils/common.utils';
import { UploadService } from '@services/upload.service';
import { API_URLS } from '@core/api-url.enum';
import { takeUntil } from 'rxjs/operators';
import { Subject } from 'rxjs';
import { environment } from '@environments/environment';
@Component({
  selector: 'app-profile-image-upload',
  templateUrl: './profile-image.component.html',
  styleUrls: ['./profile-image.component.scss']
})
export class ProfileImageUploadComponent implements OnDestroy {
  @Input() field: any;
  @Input() form: FormGroup;
  @ViewChild('fileInput') fileInput: ElementRef;

  imageFeedPreview: string | ArrayBuffer = '';
  imageChangedEvent: any = '';
  croppedImage: string = '';
  isOpen = false;
  imageUploadToS3: any[] = [];
  feedPreviewOrientation = FEED_PREVIEW_IMAGE_ORIENTATION.PORTRAIT;
  feedPreviewType = FEED_PREVIEW_IMAGE_TYPE;
  imgWidth = 0;
  application = Application;
  percent = Application.ZERO;
  showProgress = false;
  private destroy$ = new Subject<void>();

  constructor(
    public cs: CommonService,
    @Inject(PLATFORM_ID) private platformId: Object,
    private readonly cdr: ChangeDetectorRef,
    private readonly ngZone: NgZone,
    private readonly uploadService: UploadService
  ) {
    this.subscribeClearImage();
  }

  subscribeClearImage() {
    this.cs.$clearProfileImage.subscribe((res: any) => {
      if (res) {
        this.removeVideoFeedImage();
      }
    });
  }
  async convertHeicToJpeg(e) {
    if (this.cs.isBrowser) {
      const blob: File = e;
      const heicTo = (await import('heic-to')).heicTo;
      return heicTo({
        blob,
        type: 'image/jpeg',
        quality: Number(Application.ONE)
      })
        .then(async (conversionResult: any) => {
          const newName = blob.name.replace(/\.[^/.]+$/, '.jpeg');
          return new File([conversionResult], newName, {
            lastModified: new Date().getTime(),
            type: conversionResult.type
          });
        })
        .catch(() => {
          console.log('Error ');
        });
    }
  }
  uploadImageCompress(file, quality = Application.ZERO_POINT_FOUR as number) {
    if (isPlatformBrowser(this.platformId)) {
      return new Promise(async (resolve, reject) => {
        try {
          if (
            file.name.toUpperCase().includes('.HEIC') ||
            file.name.toUpperCase().includes('.HEIF')
          ) {
            file = await this.convertHeicToJpeg(file);
          } else if (
            file.name.toUpperCase().includes('.AVIF')
          ) {
            file = await this.uploadService.convertAvifToJpeg(file);
          }
          new Compressor(file, {
            quality: quality,
            success(result) {
              // Create a new File from the compressed Blob to preserve metadata
              const compressedFile = new File([result], file.name, {
                type: result.type,
                lastModified: file.lastModified
              });
              resolve(compressedFile);
            },
            error(err) {
              reject(err);
            }
          });
        } catch (err) {
          reject(err);
        }
      });
    }
  }

  mobileFileChangeEvent(event) {
    this.imageFeedClicked(event?.[0], true);
  }

  fileChangeEvent(event: Event): void {
    if (isPlatformBrowser(this.platformId)) {
      const input = event.target as HTMLInputElement;
      const file = input.files?.[0];
      if (file && file.size < 5000000) {
        this.uploadImageCompress(file).then((res) => {
          const compressedFile: any = res;
          const reader = new FileReader();
          reader.readAsDataURL(compressedFile);
          reader.onload = (e) => {
            const image = new Image();
            image.src = e.target?.result as string;
            image.onload = (le: any) => {
              const newEvent =
                le.path || (le.composedPath && le.composedPath());
              this.imgWidth = newEvent[0].naturalWidth;
              if (
                newEvent[0].naturalWidth >= Application.IMAGE_PREVIEW_WIDTH &&
                newEvent[0].naturalHeight >= Application.IMAGE_PREVIEW_HEIGHT
              ) {
                const dataTransfer = new DataTransfer();
                dataTransfer.items.add(compressedFile);
                input.files = dataTransfer.files;
                this.imageChangedEvent = event;
                requestAnimationFrame(() => {
                  this.ngZone.run(() => {
                    this.isOpen = true;
                    this.cdr.detectChanges();
                  });
                });
              } else {
                this.cs.showErrorMessage(
                  `${validationMessage.widthGreater} ${Application.IMAGE_PREVIEW_WIDTH} ${validationMessage.pixelGreater} ${Application.IMAGE_PREVIEW_HEIGHT}  ${Fields.pixels}`
                );
              }
            };
            image.onerror = () => {
            };
          };
          reader.onerror = () => {
          };
        });
      } else {
        this.cs.showErrorMessage(validationMessage.imageMax5);
      }
    }
  }

  imageFeedClicked(croppedFile?, isMobileApp = false): void {
    if (croppedFile) {
      this.croppedImage = croppedFile;
    }
    if (isPlatformBrowser(this.platformId)) {
      this.isOpen = false;

      let feedBanner: File;
      let uploadFile: File;
      let blob: Blob;

      uploadFile = this.imageChangedEvent?.target?.files?.[Application.ZERO];
      if (isMobileApp) {
        blob = base64ToImage(croppedFile?.uri, croppedFile?.fileName);
        feedBanner = blob as File;
      } else {
        blob = base64ToFile(this.croppedImage);
        feedBanner = new File([blob], uploadFile.name, {
          type: uploadFile.type
        });
      }

      const isValid = this.cs.isValidFile(feedBanner);
      if (isValid) {
        this.uploadImageCompress(feedBanner)
          .then((res) => {
            const compressedFile: any = res;
            const fReader = new FileReader();
            fReader.onload = (event) => {
              this.ngZone.run(() => {
                this.imageFeedPreview = event.target?.result as string;
                this.cdr.detectChanges();
              });
            };
            fReader.readAsDataURL(feedBanner);
            const videoImage = `${
              Application.POST + Date.now()
            }_${compressedFile.name.toLowerCase().split(' ').join('_')}`;
            const obj = {
              file: compressedFile,
              fileName: videoImage,
              folder: BucketFolders.createPostImageFolder
            };
            this.showProgress = true;
            this.uploadService
              .uploadFileData(API_URLS.uploadFile, obj, true)
              .pipe(takeUntil(this.destroy$))
              .subscribe({
                next: (response: any) => {
                  if (response.progressStatus === Application.UPLOADING) {
                    this.percent = response.progress;
                    this.cdr.detectChanges();
                    return;
                  }
                  if (response.progressStatus === Application.COMPLETED) {
                    this.showProgress = false;
                    this.percent = Application.ZERO;
                    const uploadedUrl = environment.s3Config.bucketUrl +
                      BucketFolders.createPostImageFolder + videoImage;
                    this.form.patchValue({
                      [this.field.name]: uploadedUrl
                    });
                    this.cdr.detectChanges();
                  }
                },
                error: () => {
                  this.showProgress = false;
                  this.percent = Application.ZERO;
                  this.cs.showErrorMessage(Messages.SOMETHING_WENT_WRONG);
                }
              });
          })
          .catch(() => {
          });
      } else {
        this.removeVideoFeedImage();
        this.cs.showErrorMessage(Messages.INVALID_FILE_TYPE);
      }
    }
  }

  removeVideoFeedImage(): void {
    this.form.get(this.field.name)?.reset();
    this.imageFeedPreview = '';
    this.imageUploadToS3 = [];
  }

  cancelCrop(): void {
    this.isOpen = false;
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
